import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { isEqual } from 'lodash';
import { __DEV__ } from '@apollo/client/utilities/globals';
interface RegisteredForm {
	getFormMethods: () => UseFormReturn<any>;
}

interface RegisteredFormByKey {
	key: string;
	getFormMethods?: () => UseFormReturn<any>;
}
export interface ModalContextProps {
	registerForm: (getFormMethods: () => UseFormReturn<any>) => void;
	unregisterForm: (getFormMethods: () => UseFormReturn<any>) => void;
	registerFormByKey: (
		key: string,
		getFormMethods: () => UseFormReturn<any>,
	) => void;
	checkIfModalCanBeClosed: () => boolean;
	checkIfModalCanBeClosedByKeys: (keys: string[]) => boolean;
	onModalClose: () => void;
	setModalProperties: ({ onModalClose }: ModelProperties) => void;
	unregisterFormsByKey: (keys: string[]) => void;
}

export interface ModalContextProviderProps {
	values?: ModalContextProps;
	children?: React.ReactChild | React.ReactChild[];
}

export interface ModelProperties {
	onModalClose?: () => void;
}

export const ModalContext = React.createContext<ModalContextProps>({
	registerForm: () => {},
	registerFormByKey: () => {},
	checkIfModalCanBeClosed: () => false,
	unregisterForm: () => {},
	checkIfModalCanBeClosedByKeys: () => false,
	onModalClose: () => {},
	setModalProperties: () => {},
	unregisterFormsByKey: () => {},
});

export const useModalContext: () => ModalContextProps | null = () => {
	const context = React.useContext<ModalContextProps>(ModalContext);
	if (!context) {
		return null;
	}
	return context;
};

export function ModalContextProvider({
	values,
	children,
}: ModalContextProviderProps) {
	const [registeredForms, setRegisteredForms] = useState<
		RegisteredForm[] | undefined
	>(undefined);

	const [registeredFormsByKey, setRegisteredFormsByKey] = useState<
		RegisteredFormByKey[] | undefined
	>(undefined);
	const [modalProperties, setModalProperties] = useState<ModelProperties>({});

	const registerForm = (getFormMethods: () => UseFormReturn<any>) => {
		const newForm: RegisteredForm = {
			getFormMethods: getFormMethods,
		};
		setRegisteredForms(prevState => {
			if (prevState) {
				return [...prevState, ...[newForm]];
			}
			return [...[newForm]];
		});
	};

	const unregisterForm = (getFormMethods: () => UseFormReturn<any>) => {
		setRegisteredForms(prevState =>
			prevState?.filter(item => item.getFormMethods() !== getFormMethods()),
		);
	};

	const registerFormByKey = (
		key: string,
		getFormMethods: () => UseFormReturn<any>,
	) => {
		setRegisteredFormsByKey(prevState => {
			const filteredPrevState = prevState?.filter(item => item.key !== key);
			if (filteredPrevState) {
				return [...filteredPrevState, ...[{ key, getFormMethods }]];
			}
			return [...[{ key, getFormMethods }]];
		});
	};

	const checkIfModalCanBeClosed = () => {
		const somethingInProgress = registeredForms?.some(item => {
			const formMethods = item.getFormMethods();
			let hasChange = false;
			for (const key of Object.keys(formMethods.control._formValues)) {
				if (formMethods.control._defaultValues[key] != undefined) {
					if (
						!isEqual(
							formMethods.control._formValues[key],
							formMethods.control._defaultValues[key],
						)
					) {
						hasChange = true;
					}
				} else {
					if (formMethods.control._formValues[key] != undefined) {
						hasChange = true;
					}
				}
			}
			return hasChange;
		});
		return !somethingInProgress;
	};

	const checkIfModalCanBeClosedByKeys = (keys: string[]) => {
		const forms = registeredFormsByKey?.filter(item =>
			keys.some(key => key === item.key),
		);

		const somethingInProgress = forms?.some(item => {
			const formMethods = item.getFormMethods?.();
			if (!formMethods) {
				return true;
			}
			let hasChange = false;

			if (__DEV__) {
				console.log(
					'ModalContext: _formValues: ',
					formMethods.control._formValues,
				);
				console.log(
					'ModalContext: _defaultValues: ',
					formMethods.control._defaultValues,
				);
			}
			for (const key of Object.keys(formMethods.control._formValues)) {
				if (formMethods.control._defaultValues[key] != undefined) {
					if (
						!isEqual(
							formMethods.control._formValues[key],
							formMethods.control._defaultValues[key],
						)
					) {
						hasChange = true;
					}
				} else {
					if (formMethods.control._formValues[key] != undefined) {
						hasChange = true;
					}
				}
			}
			return hasChange;
		});
		return !somethingInProgress;
	};

	const unregisterFormsByKey = (keys: string[]) => {
		setRegisteredFormsByKey(prevState =>
			prevState?.filter(item => !keys.some(key => key === item.key)),
		);
	};

	return (
		<ModalContext.Provider
			value={{
				registerForm,
				registerFormByKey,
				checkIfModalCanBeClosed,
				unregisterForm,
				checkIfModalCanBeClosedByKeys,
				onModalClose: () => modalProperties?.onModalClose?.(),
				setModalProperties,
				unregisterFormsByKey,
			}}
		>
			{children}
		</ModalContext.Provider>
	);
}
